import { useCallback, useMemo, useState } from 'react';

import {
  ColumnDef,
  PaginationState,
  RowData,
  RowSelectionState,
  SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { createStore, useSetAtom } from 'jotai';

import { tableFormattedSearchObjectAtom, tableSearchResetAtom } from '../Search/store';
import { useTableSort } from '../Search/useTableSearch';
import {
  headerSegmentedControlValueAtom,
  setHeaderSegmentedControlAtom,
} from '../TableHeader/atoms';
import { TableProvider } from '../table.context';
import { TablePdfOptions } from '../types/tablePdf.types';
import { ExportOptions, TableRootProps } from '../types/types';
import { UrlSortingClient } from './UrlSortingClient';

type paginationMeta = {
  total?: number;
  limit: number;
  has_more?: boolean;
};
// Hook for creating and using Pagination
// This is a hook because it may be....used in multiple places. Not sure yet.
// Would like to standardized the way this is being done.
const InitialPaginationState = {
  pageIndex: 0,
  pageSize: 25,
};
const usePagination = ({ meta }: { meta?: paginationMeta }) => {
  const [{ pageIndex, pageSize }, setPagination] =
    useState<PaginationState>(InitialPaginationState);

  const resetPagination = useCallback(() => {
    setPagination(InitialPaginationState);
  }, []);

  const hasPaginationMeta = Boolean(meta);

  /**
   * Calculate the page count based on the meta data
   *
   * Note: This could return NAN if total & limit are both 0
   */
  const pageCount = useMemo(() => {
    if (!meta?.limit || !meta?.total) return NaN;
    const count = Math.ceil(meta?.total / meta?.limit);
    return count;
  }, [meta?.limit, meta?.total]);

  /**
   * Calculate the final page count based on the meta data
   */
  const finalPageCount = useMemo(() => {
    // If hasPaginationMeta is falsy, we don't want to show the pagination at all
    if (hasPaginationMeta == false) return 0;

    // If pageCount is a valid number, safely return that
    if (isNaN(pageCount) == false) return pageCount;

    // If we didn't have a pageCount, but we know there are no more pages, return the current pageIndex so it shows as the total
    if (meta?.has_more === false) return pageIndex;

    // If page count is NaN, we want to show the pagination, but don't know how much to show
    // If we have had a meta passed in, but we simply don't know the limit or total, we can use a -1 identifier to show the pagination but not the pageButton
    // OtherWise, we'll return 0 to not show the pagination at all.
    return hasPaginationMeta ? -1 : 0;
  }, [hasPaginationMeta, meta?.has_more, pageCount, pageIndex]);

  return {
    pagination: { pageIndex, pageSize, page: pageIndex, limit: pageSize },
    page: pageIndex,
    setPagination,
    pageCount: finalPageCount,
    resetPagination,
  };
};

export function TableRoot<
  TData extends RowData,
  TMeta extends Record<string, unknown> = Record<string, never>,
>({
  children,
  initialState,
  tableMeta,
  getRowId,
  enableRowSelection,
}: TableRootProps<TData, TMeta>) {
  const [store] = useState(() => createStore());
  const [data, setData] = useState<TData[]>([]);
  const [_columns, setColumns] = useState<ColumnDef<TData, any>[]>([]);
  const [meta, setMeta] = useState<paginationMeta | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<undefined | boolean>();
  const [exportOptions, setExportOptions] = useState<undefined | ExportOptions<any, any>>();
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);
  const resetTableSearches = useSetAtom(tableSearchResetAtom, { store: store });
  const { pagination, page, setPagination, pageCount, resetPagination } = usePagination({ meta });

  // Handle allowing dynamic Table Visibility Changes
  const [columnVisibility, setColumnVisibility] = useState(initialState?.columnVisibility ?? {});
  const [pdfOptions, setPdfOptions] = useState<undefined | TablePdfOptions>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // This is fine to be here, really it's what is driving the ui table.
  const table = useReactTable({
    data: data,
    columns: _columns,
    pageCount: pageCount,
    initialState: initialState,
    state: {
      pagination: pagination,
      sorting,
      columnVisibility: columnVisibility,
      rowSelection,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,

    manualPagination: true,
    manualSorting: true,
    // debugTable: false,
    meta: tableMeta,

    getRowId,
    enableRowSelection,
    onRowSelectionChange: setRowSelection, //hoist up the row selection state
  });

  /** Use the columns as a way to ensure we update the sorting when the columns change */
  useTableSort(
    { store, sorting, getAllColumns: table.getAllColumns },
    // This needs to be memoized to ensure we're only having the dependencies update their reference when the columns change and not on every render of the table
    useMemo(() => [_columns], [_columns])
  );

  return (
    <TableProvider
      value={{
        table,
        pagination,
        data,
        setData,
        columns: _columns,
        setColumns,
        page,
        pageCount,
        setMeta,
        sorting,
        setSorting,
        atomStore: store,
        isLoading,
        setIsLoading,
        exportOptions,
        setExportOptions,
        tableSearchObjectAtom: tableFormattedSearchObjectAtom,
        resetTableSearches,
        resetPagination,
        headerSegmentedControlValueAtom,
        setHeaderSegmentedControlAtom,
        setColumnVisibility,
        pdfOptions,
        setPdfOptions,
        rowSelection,
        setRowSelection,
      }}
    >
      <UrlSortingClient
        getAllColumns={table.getAllColumns}
        setSorting={setSorting}
        resetSorting={table.resetSorting}
      />
      {children}
    </TableProvider>
  );
}
